import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { MarginCheckResult } from '../../api/marginLadder';
import useAuthStore from '../../store/zustand/auth';
import { ROLES } from '../../helpers/roles';

interface Props {
  currentMargin: number | undefined | null; // 0.0–1.0
  monthlyValue: number | undefined | null; // USD
}

const TIER_STYLES: Record<
  string,
  { background: string; color: string; label: string }
> = {
  acceptable: {
    background: '#FFF4DC',
    color: '#B47A00',
    label: 'Acceptable'
  },
  below_floor: {
    background: '#FDE2E2',
    color: '#B00020',
    label: 'Below floor'
  },
  floor: {
    background: '#FDF0E2',
    color: '#C06A00',
    label: 'At floor'
  },
  win: {
    background: '#E1F5E8',
    color: '#1D7A3B',
    label: 'Win'
  }
};

// Zone colors for the gauge track. Below-floor zone is saturated red so it
// reads as an alarm state; win zone is saturated green; floor / acceptable
// sit in the warning range.
const ZONE_FILL = {
  acceptable: '#FFD874',
  belowFloor: '#E57373',
  floor: '#F5B062',
  win: '#66BB6A'
};

const pct = (n: number) => `${(n * 100).toFixed(2)}%`;

interface GaugeProps {
  acceptableMargin: number;
  currentMargin: number;
  floorMargin: number;
  winMargin: number;
}
const MarginGaugeDefaults = {};
const MarginGauge: React.FC<GaugeProps> = function MarginGauge({
  acceptableMargin,
  currentMargin,
  floorMargin,
  winMargin
}) {
  // Gauge spans from 0% to (winMargin + 10pp) so the "Win" zone has visible
  // trailing room. Current margin gets clamped into view.
  const maxMargin = Math.max(winMargin + 0.1, currentMargin + 0.05);
  const toPercent = (m: number) => Math.min(100, Math.max(0, (m / maxMargin) * 100));
  const floorPos = toPercent(floorMargin);
  const accPos = toPercent(acceptableMargin);
  const winPos = toPercent(winMargin);
  const currentPos = toPercent(currentMargin);
  // Current tier for accent color on the needle.
  let currentTier: 'acceptable' | 'belowFloor' | 'floor' | 'win' = 'win';
  if (currentMargin < floorMargin) currentTier = 'belowFloor';
  else if (currentMargin < acceptableMargin) currentTier = 'floor';
  else if (currentMargin < winMargin) currentTier = 'acceptable';
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box
        sx={{
          background:
            `linear-gradient(to right,` +
            ` ${ZONE_FILL.belowFloor} 0%,` +
            ` ${ZONE_FILL.belowFloor} ${floorPos}%,` +
            ` ${ZONE_FILL.floor} ${floorPos}%,` +
            ` ${ZONE_FILL.floor} ${accPos}%,` +
            ` ${ZONE_FILL.acceptable} ${accPos}%,` +
            ` ${ZONE_FILL.acceptable} ${winPos}%,` +
            ` ${ZONE_FILL.win} ${winPos}%,` +
            ` ${ZONE_FILL.win} 100%)`,
          borderRadius: '6px',
          height: 10,
          position: 'relative',
          width: '100%'
        }}
      >
        {/* Current-margin needle */}
        <Box
          sx={{
            bgcolor: ZONE_FILL[currentTier],
            border: '2px solid #fff',
            borderRadius: '50%',
            boxShadow: '0 0 2px rgba(0,0,0,0.35)',
            height: 16,
            left: `calc(${currentPos}% - 8px)`,
            position: 'absolute',
            top: -3,
            width: 16
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 0.5
        }}
      >
        <Typography color="secondary.400" sx={{ fontSize: '0.7rem' }} variant="body_100">
          0%
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }} variant="body_100">
          {pct(currentMargin)}
        </Typography>
        <Typography color="secondary.400" sx={{ fontSize: '0.7rem' }} variant="body_100">
          {pct(maxMargin)}
        </Typography>
      </Box>
      <Typography color="secondary.400" sx={{ fontSize: '0.7rem', mt: 0.5 }} variant="body_100">
        Floor {pct(floorMargin)} · Acc {pct(acceptableMargin)} · Win {pct(winMargin)}
      </Typography>
    </Box>
  );
};
MarginGauge.defaultProps = MarginGaugeDefaults;

/**
 * Calculator v3 §12.4 — Margin Ladder chip on the Analysis dashboard.
 *
 * Reads the tenant's ladder (or global default) via POST /margin-ladder/check
 * and renders a color-coded tier chip next to the existing profit tiles.
 * Approval-required state (below_floor) shows in red. Hover for the full
 * message + tier thresholds.
 *
 * Hidden entirely when the tenant hasn't opted in to calculator_v3 (the
 * endpoint 403s and the component just silently renders nothing).
 */
const MarginLadderTile: React.FC<Props> = function MarginLadderTile({
  currentMargin,
  monthlyValue
}) {
  const [result, setResult] = useState<MarginCheckResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [hidden, setHidden] = useState<boolean>(false);
  const navigate = useNavigate();
  const roles = (useAuthStore((s) => s.roles) ?? []) as string[];
  const canManageThresholds =
    roles.includes(ROLES.ADMINISTRATOR) || roles.includes(ROLES.SUPER_USER);

  useEffect(() => {
    if (
      currentMargin === undefined ||
      currentMargin === null ||
      monthlyValue === undefined ||
      monthlyValue === null ||
      monthlyValue <= 0
    ) {
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const res = await api.MarginLadderApi.check(
          monthlyValue,
          currentMargin
        );
        setResult(res);
        setHidden(false);
      } catch (err: any) {
        // 403 = tenant doesn't have calc v3 — just hide the tile.
        if (err?.response?.status === 403) setHidden(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentMargin, monthlyValue]);

  if (hidden) return null;

  return (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'common.white',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: { md: 'row', xs: 'column' },
        gap: 3,
        p: 2.5
      }}
    >
      <Box sx={{ minWidth: 160 }}>
        <Typography
          color="secondary.400"
          sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}
          variant="body_100"
        >
          Margin Tier
        </Typography>
        {result && (
          <Tooltip arrow title={result.message}>
            <Box
              sx={{
                bgcolor: TIER_STYLES[result.tier]?.background,
                borderRadius: '14px',
                color: TIER_STYLES[result.tier]?.color,
                display: 'inline-block',
                fontSize: '0.85rem',
                fontWeight: 700,
                mt: 0.75,
                px: 1.5,
                py: 0.5,
                width: 'fit-content'
              }}
            >
              {TIER_STYLES[result.tier]?.label ?? result.tier}
              {result.requiresApproval ? ' · approval needed' : ''}
            </Box>
          </Tooltip>
        )}
      </Box>
      {loading && !result && (
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="body_100">Checking tier…</Typography>
        </Box>
      )}
      {result && (
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MarginGauge
            acceptableMargin={result.tierRow.acceptableMargin}
            currentMargin={result.currentMargin}
            floorMargin={result.tierRow.floorMargin}
            winMargin={result.tierRow.winMargin}
          />
        </Box>
      )}
      {result && canManageThresholds && (
        <Tooltip arrow placement="top" title="Manage margin thresholds">
          <IconButton
            onClick={() =>
              navigate('/app/company-settings', { state: { tab: 4 } })
            }
            size="small"
          >
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default MarginLadderTile;
